import { useState } from "react";
import { Modal, Typography, IconButton, Button, InputBase, CircularProgress } from "@mui/material";
import styles from "./stylesDepositModal.module.scss";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import ShieldAttention from "@/assets/svg/ShieldAttention";
import CloseBtn from "@/assets/svg/CloseBtn";
import { useGetDepositLimitsQuery, useCreateDepositInvoiceMutation } from "@/store/api/walletApi";
import { formatMoney } from "@/helpers/formatMoney";

interface Props {
  onClose: () => void;
  userEmail?: string;
}

type Step = "form" | "submitting";

const MIN_DEPOSIT = 1;

const DepositModal = ({ onClose }: Props) => {
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState<Step>("form");

  const [apiError, setApiError] = useState<string | null>(null);

  const { data: limits, isLoading: limitsLoading } = useGetDepositLimitsQuery();
  const [createInvoice] = useCreateDepositInvoiceMutation();

  const maxAvailable = limits?.remainingAmount ?? 0;
  const numAmount = Number(amount);
  const isSubmitting = step === "submitting";
  const HARD_MAX = 1000;
  const allowedMax = Math.min(maxAvailable, HARD_MAX);
  const getAmountError = (): string | null => {
    if (!amount) return null;

    if (isNaN(numAmount) || numAmount <= 0) {
      return "Enter a valid amount";
    }

    if (numAmount < MIN_DEPOSIT) {
      return `Minimum deposit is $${MIN_DEPOSIT}`;
    }

    if (numAmount > allowedMax) {
      return `Maximum allowed is $${formatMoney(allowedMax)}`;
    }

    return null;
  };

  const isLimitReached = !limitsLoading && maxAvailable <= 0;

  const amountError = isLimitReached
    ? "Your limit has been reached. It will reset next month."
    : getAmountError();
  const isFormValid = !!amount && !amountError && !limitsLoading && !isLimitReached;

  const handleAmountChange = (value: string) => {
    setApiError(null);

    let sanitized = value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1");

    const numeric = Number(sanitized);

    if (!isNaN(numeric)) {
      if (numeric > allowedMax) {
        sanitized = String(allowedMax);
      }
    }

    setAmount(sanitized);
  };
  const handleSubmit = async () => {
    if (!isFormValid) return;
    setStep("submitting");
    setApiError(null);
    try {
      const result = await createInvoice({
        amount: numAmount,
      }).unwrap();
      window.open(result.invoice, "_blank", "noopener,noreferrer");
      onClose();
    } catch (err: any) {
      setApiError(err?.data?.message ?? "Something went wrong. Please try again.");
      setStep("form");
    }
  };

  return (
    <Modal open onClose={isSubmitting ? undefined : onClose}>
      <div className={styles.overlay} onClick={isSubmitting ? undefined : onClose}>
        <div className={styles.modalWrapper} onClick={(e) => e.stopPropagation()}>
          {isSubmitting && (
            <>
              <div className={styles.blurOverlay} />
              <div className={styles.loaderOverlay}>
                <CustomLoader loading isModal={false} size={42} />
              </div>
            </>
          )}

          <div className={`${styles.modal} ${isSubmitting ? styles.modalBlurred : ""}`}>
            <div className={styles.header}>
              <Typography className={styles.title}>Deposit Funds</Typography>
              <IconButton onClick={onClose} className={styles.Btn} disabled={isSubmitting}>
                <CloseBtn />
              </IconButton>
            </div>

            <>
              <div className={styles.amoutBlock}>
                <div className={styles.amoutShield}>
                  <ShieldAttention />
                </div>
                {limitsLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography className={styles.amoutText}>
                    Monthly deposit limit is{" "}
                    <span className={styles.amoutMax}>
                      ${formatMoney(limits?.monthlyLimit ?? 1000)} USDC
                    </span>
                    . You have{" "}
                    <span className={styles.amoutMax}>${formatMoney(maxAvailable)}</span>{" "}
                    remaining this month.
                  </Typography>
                )}
              </div>

              <Typography className={styles.feeNote}>Note: a 1% deposit fee applies.</Typography>

              <div className={styles.field}>
                <Typography className={styles.label}>Amount (USDC)</Typography>
                <div
                  className={`${styles.amountWrapper} ${amountError ? styles.amountWrapperError : ""}`}
                >
                  <span className={styles.currency}>$</span>
                  <InputBase
                    inputProps={{
                      max: allowedMax,
                    }}
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className={styles.amountInput}
                    inputMode="decimal"
                    disabled={isSubmitting || limitsLoading || isLimitReached}
                    autoFocus
                  />
                </div>

                <div className={styles.bottomRow}>
                  <Typography
                    className={`${styles.errorInline} ${!amountError ? styles.errorHidden : ""}`}
                  >
                    {amountError || " "}
                  </Typography>
                  {!isLimitReached && (
                    <Typography className={styles.max}>
                      Max available: {limitsLoading ? "..." : `$${formatMoney(maxAvailable)}`}
                    </Typography>
                  )}
                </div>
              </div>

              <Typography
                className={`${styles.errorMsg} ${!apiError ? styles.errorMsgHidden : ""}`}
              >
                {apiError || " "}
              </Typography>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
                className={`${styles.button} ${!isFormValid ? styles.buttonDisabled : ""}`}
              >
                Next
              </Button>
            </>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DepositModal;
